import React, { Fragment, useContext, useState } from 'react'
import FormControl from 'react-bootstrap/lib/FormControl';
import Papa from 'papaparse';
import FrappButton from '../../utilities/FrappButton'
import CustomTooltip from '../../utilities/ToolTip'
import { ISContext } from './ISContext'
import ApiActions from '../../../actions/ApiActions';

// Worded for the content team; the request/response details for developers sit in the ⓘ tooltip
const SOURCES = [
    {
        value: 'static',
        title: 'We maintain the list',
        description: 'Type the phone numbers and names here. Edit them when people change.'
    },
    {
        value: 'api',
        title: "The client's system sends the list",
        description: "Paste the link the client gave us. The app loads their latest list during the call.",
        developerNote: 'GET request. Must return [{ "phone": "9876543210", "label": "Name" }]. Phone is 10 digits without +91.'
    },
    {
        value: 'browser',
        title: 'The caller picks on a web page',
        description: 'Paste the page link. The app opens it and the caller chooses who to transfer to.',
        developerNote: 'The page must return [{ "phone": "9876543210", "label": "Name" }] to the app. Phone is 10 digits without +91.'
    }
]

// small inline icons; they use the button's text colour
const UploadIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" />
    </svg>
)
const DownloadIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
    </svg>
)

const TrashIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
    </svg>
)

const emptyContact = () => ({ phone: '', label: '' })

// apiUrl and browserUrl are separate so switching source never carries one URL into the other
const emptyConfig = () => ({
    source: 'static',
    contacts: [emptyContact()],
    apiUrl: '',
    browserUrl: '',
    headers: []
})

// node.data.callTransfer -> form state. All fields are kept so switching source back and forth keeps what was typed
const toFormState = (callTransfer) => {
    if (!callTransfer) {
        return emptyConfig()
    }
    return {
        source: callTransfer.source,
        contacts: callTransfer.contacts && callTransfer.contacts.length > 0
            ? callTransfer.contacts.map(contact => ({ phone: contact.phone, label: contact.label }))
            : [emptyContact()],
        apiUrl: callTransfer.source === 'api' ? callTransfer.url : '',
        browserUrl: callTransfer.source === 'browser' ? callTransfer.url : '',
        headers: callTransfer.headers
            ? Object.entries(callTransfer.headers).map(([name, value]) => ({ name, value }))
            : []
    }
}

// form state -> request body. Only the fields of the chosen source are sent; the backend rejects the rest
const toCallTransfer = (config) => {
    if (config.source === 'static') {
        return {
            source: 'static',
            contacts: config.contacts.map(contact => ({ phone: contact.phone.trim(), label: contact.label.trim() }))
        }
    }
    if (config.source === 'browser') {
        return { source: 'browser', url: config.browserUrl.trim() }
    }
    const callTransfer = { source: 'api', url: config.apiUrl.trim() }
    if (config.headers.length > 0) {
        callTransfer.headers = Object.fromEntries(config.headers.map(header => [header.name.trim(), header.value]))
    }
    return callTransfer
}

// Upload only: turns common Indian formats into the 10 digits the backend expects.
// "+91 98765 43210", "919876543210" and "09876543210" all become "9876543210".
// Anything else is left as typed so the row check flags it; typed numbers are never changed.
const cleanPhone = (raw) => {
    const original = String(raw || '').trim()
    let phone = original.replace(/[\s\-()]/g, '')
    if (phone.startsWith('+91')) {
        phone = phone.slice(3)
    } else if (/^91\d{10}$/.test(phone)) {
        phone = phone.slice(2)
    } else if (/^0\d{10}$/.test(phone)) {
        phone = phone.slice(1)
    }
    return { phone, cleaned: phone !== original }
}

// One message per contact row ('' when the row is fine), so the form can mark the exact rows.
// Same rules as callTransferValidation in frapp-backbone.
const getContactErrors = (contacts) => {
    const firstRowOfPhone = {}
    return contacts.map((contact, index) => {
        const phone = contact.phone.trim()
        if (!phone) {
            return 'Phone number is missing'
        }
        if (/e\+/i.test(phone)) {
            return 'Excel changed this number. Format the phone column as Text in Excel and export again.'
        }
        if (!/^\d{10}$/.test(phone)) {
            return `"${phone}" is not a 10-digit number`
        }
        if (!contact.label.trim()) {
            return 'Name is missing'
        }
        if (firstRowOfPhone[phone] !== undefined) {
            return `Duplicate of row ${firstRowOfPhone[phone] + 1}`
        }
        firstRowOfPhone[phone] = index
        return ''
    })
}

// Returns the problem to show next to Save, or '' when the form can be saved
const getValidationError = (config) => {
    if (config.source === 'static') {
        if (config.contacts.length === 0) {
            return 'Add at least one number'
        }
        const rowErrors = getContactErrors(config.contacts)
        const badRows = rowErrors.filter(Boolean).length
        if (badRows === 1) {
            const index = rowErrors.findIndex(Boolean)
            return `Row ${index + 1}: ${rowErrors[index]}`
        }
        return badRows > 1 ? `${badRows} rows need fixing (marked in red)` : ''
    }

    const url = config.source === 'api' ? config.apiUrl : config.browserUrl
    if (!/^https:\/\/\S+$/.test(url.trim())) {
        return 'Paste the full link, starting with https://'
    }
    if (config.source === 'api') {
        const seenHeaders = new Set()
        for (const header of config.headers) {
            const name = header.name.trim()
            if (!name) {
                return 'Access key name cannot be empty'
            }
            if (seenHeaders.has(name.toLowerCase())) {
                return `Access key ${name} is added more than once`
            }
            seenHeaders.add(name.toLowerCase())
        }
    }
    return ''
}

// How a config is described in the replace confirmation, e.g. "the 12 numbers saved here"
const describeCallTransfer = (callTransfer) => {
    if (callTransfer.source === 'static') {
        const count = callTransfer.contacts.length
        return `the ${count} number${count === 1 ? '' : 's'} saved here`
    }
    return callTransfer.source === 'api' ? "the client's link" : 'the page link'
}

const CallTransferForm = (props) => {
    const { currentNodeData, setCurrentNodeData, allScripts, setAllScripts, setElements, setElements1, setAllScripts2, teleprojectdata } = useContext(ISContext)
    const getScriptsData = props.formData
    const [selectedScript, setSelectedScript] = useState(getScriptsData[0])
    const [config, setConfig] = useState(toFormState(getScriptsData[0].nodeData.data.callTransfer))
    const [showHeaderValues, setShowHeaderValues] = useState(false)
    // errors are shown only after the first Save attempt, not while the user is still filling the form
    const [showErrors, setShowErrors] = useState(false)
    // 'replace' (saving a different source than the saved one) or 'remove'; shows the confirmation bar
    const [pendingAction, setPendingAction] = useState(null)
    const [isSaving, setIsSaving] = useState(false)
    const [isError, setError] = useState('')
    const [isSuccess, setSuccess] = useState('')

    const savedCallTransfer = selectedScript.nodeData.data.callTransfer
    const validationError = getValidationError(config)

    const resetMessage = () => {
        if (isError) {
            setError('')
        }
        if (isSuccess) {
            setSuccess('')
        }
    }

    const toggle = (e) => {
        if (isSaving) {
            return
        }
        resetMessage()
        setSelectedScript(e)
        setConfig(toFormState(e.nodeData.data.callTransfer))
        setShowErrors(false)
        setPendingAction(null)
    }

    const updateConfig = (changes) => {
        resetMessage()
        setPendingAction(null)
        setConfig({ ...config, ...changes })
    }

    const copyFromLanguage = (e) => {
        setConfig(toFormState(e.nodeData.data.callTransfer))
        setError('')
        setSuccess(`Copied from ${e.name}. Click Save to keep it for ${selectedScript.name}.`)
    }

    const updateContact = (key, value, index) => {
        const contacts = config.contacts.map((contact, idx) => idx === index ? { ...contact, [key]: value } : contact)
        updateConfig({ contacts })
    }

    const removeContact = (index) => {
        updateConfig({ contacts: config.contacts.filter((contact, idx) => idx !== index) })
    }

    const updateHeader = (key, value, index) => {
        const headers = config.headers.map((header, idx) => idx === index ? { ...header, [key]: value } : header)
        updateConfig({ headers })
    }

    const removeHeader = (index) => {
        updateConfig({ headers: config.headers.filter((header, idx) => idx !== index) })
    }

    // The file replaces the list, the same way the data collection options upload does.
    // Headers must be exactly "phone" and "label"; other columns are ignored.
    const uploadContactsCsv = (event) => {
        const file = event.target.files[0]
        event.target.value = ''
        if (!file) {
            return
        }
        const reader = new FileReader()
        reader.onload = (e) => {
            // Excel's "CSV UTF-8" starts the file with an invisible BOM, which would make the first header "\uFEFFphone"
            const text = String(e.target.result).replace(/^\uFEFF/, '')
            const { data, meta } = Papa.parse(text, { header: true, skipEmptyLines: true })
            const fields = meta.fields || []
            if (!fields.includes('phone') || !fields.includes('label')) {
                setSuccess('')
                setError(`The first row must have the columns phone and label (lowercase). Columns found: ${fields.join(', ') || 'none'}`)
                return
            }

            let cleanedCount = 0
            const contacts = data
                .filter(row => String(row.phone || '').trim() || String(row.label || '').trim())
                .map(row => {
                    const { phone, cleaned } = cleanPhone(row.phone)
                    if (cleaned) {
                        cleanedCount++
                    }
                    return { phone, label: String(row.label || '').trim() }
                })
            const badRows = getContactErrors(contacts).filter(Boolean).length

            setConfig({ ...config, contacts: contacts.length > 0 ? contacts : [emptyContact()] })
            setPendingAction(null)
            setShowErrors(badRows > 0)
            setError('')
            setSuccess(`Loaded ${contacts.length} numbers from ${file.name}.`
                + (cleanedCount > 0 ? ` Cleaned ${cleanedCount} (removed +91, a leading 0 or spaces).` : '')
                + (badRows > 0 ? '' : ' Click Save to keep them.'))
        }
        reader.readAsText(file)
    }

    const downloadTemplate = () => {
        downloadCsv(Papa.unparse({ fields: ['phone', 'label'], data: [] }), 'call-transfer-template.csv')
    }

    const downloadCsv = (csv, fileName) => {
        const blob = new Blob([csv], { type: 'text/csv' })
        const blobUrl = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = blobUrl
        link.download = fileName
        link.style.display = 'none'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(blobUrl)
    }

    const downloadContactsCsv = (contacts) => {
        const csv = Papa.unparse({
            fields: ['phone', 'label'],
            data: contacts.map(contact => [contact.phone, contact.label])
        })
        downloadCsv(csv, `${teleprojectdata.title}-${selectedScript.name}-call-transfer-numbers.csv`)
    }

    // callTransfer: null removes it from the node of the selected language
    const saveCallTransfer = (callTransfer) => {
        resetMessage()
        setPendingAction(null)
        let notANewNode = props.allScripts2.length > 0 ? props.allScripts2.find(e => e.id == props.formsDataCollection.nodeData.id) : ''
        if (!notANewNode) {
            alert('Please save the node first and then add call transfer to it')
            return
        }

        const dataToSend = {
            "script": selectedScript.id,
            "nodeId": props.formsDataCollection.nodeData.id,
            "callTransfer": callTransfer
        }

        setIsSaving(true)
        ApiActions.createCallTransfer(dataToSend)
            .then(resp => {
                // Keep the editor's copy in sync: the Node Editor SUBMIT (saveScript) writes the whole
                // script from this state, so a stale copy would put the old node back
                let result = allScripts.map(each => {
                    if (each.id == resp.data.id) {
                        each.script = resp.data.script
                    }
                    return each
                })
                setAllScripts([...result])
                result.map(elem => {
                    if (elem.defaultScript) {
                        setElements(elem.script)
                        setElements1([...elem.script])
                        setAllScripts2(elem.script)
                    }
                })

                // use what the backend stored (trimmed, extra fields dropped), not what was sent
                const savedNode = resp.data.script.find(node => node.id == dataToSend.nodeId)
                const saved = savedNode && savedNode.data ? savedNode.data.callTransfer : undefined
                let b = currentNodeData.map((e) => {
                    if (e.id == dataToSend.script) {
                        if (saved) {
                            e.nodeData.data.callTransfer = saved
                        } else {
                            delete e.nodeData.data.callTransfer
                        }
                    }
                    return e
                })
                setCurrentNodeData([...b])
                setConfig(toFormState(saved))
                setShowErrors(false)
                setIsSaving(false)
                setSuccess(saved
                    ? `Call transfer for ${resp.data.scriptLanguage} was saved`
                    : `Call transfer was removed from ${resp.data.scriptLanguage}`)
            })
            .catch(err => {
                const message = err && err.response && err.response.data && err.response.data.error
                setIsSaving(false)
                setError(message ? message : 'Something went wrong while saving. Please try again.')
            })
    }

    const handleSave = () => {
        if (validationError) {
            setShowErrors(true)
            return
        }
        if (savedCallTransfer && savedCallTransfer.source !== config.source) {
            setPendingAction('replace')
            return
        }
        saveCallTransfer(toCallTransfer(config))
    }

    const renderSourceChoice = () => {
        return <div className='snapshot-choice'>
            {SOURCES.map(s => (
                <label key={s.value} className={config.source === s.value ? 'selected' : ''}>
                    <input
                        type="radio"
                        name="call-transfer-source"
                        checked={config.source === s.value}
                        disabled={isSaving}
                        onChange={() => updateConfig({ source: s.value })}
                    />
                    <div>
                        <b>
                            {s.title}
                            {s.developerNote ?
                                <CustomTooltip placement={'right'} description={s.developerNote}
                                    component={<img src={require("../../../assets/images/black-info-icon.svg")} className='call-transfer-info' />}>
                                </CustomTooltip> : null}
                        </b>
                        <span>{s.description}</span>
                    </div>
                    {savedCallTransfer && savedCallTransfer.source === s.value ?
                        <span className='call-transfer-in-use'>✓ In use</span> : null}
                </label>
            ))}
        </div>
    }

    const renderContacts = () => {
        const rowErrors = showErrors ? getContactErrors(config.contacts) : []
        const hasAnyPhone = config.contacts.some(contact => contact.phone.trim())
        return <div className='name-value-section'>
            <div className='dotted-border bg-white'>
                <div className='contain'>
                    {config.contacts.map((contact, index) => {
                        return <Fragment key={index}>
                        <div className={rowErrors[index] ? 'name-value-row call-transfer-row-error' : 'name-value-row'}>
                            <div className='label-value'>
                                <div className='label-bold'>{index + 1}. Phone</div>
                                <FormControl
                                    className="qs-title"
                                    value={contact.phone}
                                    onChange={(e) => updateContact('phone', e.target.value, index)}
                                    placeholder="10 digits, no +91"
                                />
                            </div>
                            <div className='label-value w-70'>
                                <span className='label-bold w-10'>Name</span>
                                <FormControl
                                    className="qs-title"
                                    value={contact.label}
                                    onChange={(e) => updateContact('label', e.target.value, index)}
                                    placeholder="Shown to the caller"
                                />
                            </div>
                            <span>
                                <img src={require('../../../assets/images/del-icon-taskprops.svg')}
                                    className='delete-icon'
                                    onClick={() => removeContact(index)} />
                            </span>
                        </div>
                        {rowErrors[index] ? <div className='call-transfer-row-message'>{rowErrors[index]}</div> : null}
                        </Fragment>
                    })}
                </div>
                <div className='call-transfer-toolbar'>
                    <button type="button" className='call-transfer-btn'
                        onClick={() => updateConfig({ contacts: [...config.contacts, emptyContact()] })}>
                        + Add number
                    </button>
                    <div className='call-transfer-toolbar-right'>
                        <label className='call-transfer-btn'>
                            <UploadIcon /> Upload CSV
                            <input type="file" accept=".csv" onChange={uploadContactsCsv} style={{ display: 'none' }} />
                        </label>
                        <button type="button" className='call-transfer-btn'
                            disabled={!hasAnyPhone}
                            title={hasAnyPhone ? 'Download these numbers as a CSV' : 'Add numbers first'}
                            onClick={() => downloadContactsCsv(config.contacts)}>
                            <DownloadIcon /> Download CSV
                        </button>
                    </div>
                </div>
                <div className='call-transfer-hint'>
                    CSV columns: <b>phone</b> (10 digits, no +91) and <b>label</b> (name shown to the Service Provider). Other columns are ignored.
                    Numbers with +91, a leading 0 or spaces are cleaned on upload.
                    Don't have a file? <span className='call-transfer-link' onClick={downloadTemplate}>Download the template</span>.
                </div>
            </div>
        </div>
    }

    const renderLink = (key, label, placeholder) => {
        return <div className='call-transfer-field'>
            <label>{label}</label>
            <FormControl
                className="qs-title"
                value={config[key]}
                disabled={isSaving}
                onChange={(e) => updateConfig({ [key]: e.target.value })}
                placeholder={placeholder}
            />
        </div>
    }

    const renderHeaders = () => {
        return <div className='call-transfer-field'>
            <label>Access key <span className='call-transfer-hint'>(only if the client gave one)</span></label>
            <div className='name-value-section'>
                <div className='dotted-border bg-white'>
                    {config.headers.length > 0 ? <div className='contain'>
                        {config.headers.map((header, index) => {
                            return <div className='name-value-row' key={index}>
                                <div className='label-value'>
                                    <div className='label-bold'>Name</div>
                                    <FormControl
                                        className="qs-title"
                                        value={header.name}
                                        onChange={(e) => updateHeader('name', e.target.value, index)}
                                        placeholder="e.g. x-api-key"
                                    />
                                </div>
                                <div className='label-value w-70'>
                                    <span className='label-bold w-10'>Value</span>
                                    <FormControl
                                        className="qs-title"
                                        type={showHeaderValues ? 'text' : 'password'}
                                        value={header.value}
                                        onChange={(e) => updateHeader('value', e.target.value, index)}
                                        placeholder="The key the client gave"
                                    />
                                </div>
                                <span>
                                    <img src={require('../../../assets/images/del-icon-taskprops.svg')}
                                        className='delete-icon'
                                        onClick={() => removeHeader(index)} />
                                </span>
                            </div>
                        })}
                    </div> : null}
                    <div className='call-transfer-toolbar'>
                        <button type="button" className='call-transfer-btn'
                            onClick={() => updateConfig({ headers: [...config.headers, { name: '', value: '' }] })}>
                            + Add access key
                        </button>
                        {config.headers.length > 0 ?
                            <span className='call-transfer-link' onClick={() => setShowHeaderValues(!showHeaderValues)}>
                                {showHeaderValues ? 'Hide values' : 'Show values'}
                            </span> : null}
                    </div>
                </div>
            </div>
        </div>
    }

    const renderCopyFrom = () => {
        if (savedCallTransfer) {
            return null
        }
        const languagesWithCallTransfer = getScriptsData.filter(e => e.name !== selectedScript.name && e.nodeData.data.callTransfer)
        if (languagesWithCallTransfer.length === 0) {
            return null
        }
        return <div className='call-transfer-copy'>
            <span>{selectedScript.name} has no call transfer yet.</span>
            {languagesWithCallTransfer.map(e => (
                <span key={e.name} className='call-transfer-link' onClick={() => copyFromLanguage(e)}>Copy from {e.name}</span>
            ))}
        </div>
    }

    const renderConfirmation = () => {
        if (pendingAction === 'replace') {
            const newSource = SOURCES.find(s => s.value === config.source)
            return <div className='call-transfer-confirm'>
                <span className='call-transfer-confirm-text'>
                    Saving replaces {describeCallTransfer(savedCallTransfer)} with "{newSource.title}".
                    {savedCallTransfer.source === 'static' ?
                        <span> <span className='call-transfer-link' onClick={() => downloadContactsCsv(savedCallTransfer.contacts)}>Download the current numbers</span> first if you may need them again.</span>
                        : null}
                </span>
                <button className='call-transfer-btn' onClick={() => setPendingAction(null)}>Cancel</button>
                <button className='call-transfer-btn primary' onClick={() => saveCallTransfer(toCallTransfer(config))}>Replace and save</button>
            </div>
        }
        if (pendingAction === 'remove') {
            return <div className='call-transfer-confirm'>
                <span className='call-transfer-confirm-text'>
                    Remove call transfer from this node in {selectedScript.name}? The app will stop showing the Call Transfer button.
                </span>
                <button className='call-transfer-btn' onClick={() => setPendingAction(null)}>Cancel</button>
                <button className='call-transfer-btn danger' onClick={() => saveCallTransfer(null)}>Remove</button>
            </div>
        }
        return null
    }

    const renderForm = () => {
        return <div className="outcome-wrapper">
            <div className="feedback-form">
                <div style={{ marginTop: '2%' }}>
                    {renderCopyFrom()}
                    <div className='grey-box-wrapper'>
                        <div className='text-content'>
                            <div className='form-data'>
                                <div className='call-transfer-question'>Where does the transfer list come from?</div>
                                {renderSourceChoice()}

                                {config.source === 'static' ? renderContacts() : null}
                                {config.source === 'api' ? renderLink('apiUrl', 'Link from the client', 'https://client.example/contacts') : null}
                                {config.source === 'api' ? renderHeaders() : null}
                                {config.source === 'browser' ? renderLink('browserUrl', 'Page link', 'https://transfer.example/pick') : null}
                            </div>
                        </div>
                    </div>

                    {renderConfirmation()}

                    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'baseline', gap: '10px' }}>
                        {savedCallTransfer && !isSaving ?
                            <button type="button" className='call-transfer-btn ghost-danger call-transfer-remove'
                                disabled={!!pendingAction}
                                onClick={() => setPendingAction('remove')}>
                                <TrashIcon /> Remove from {selectedScript.name}
                            </button> : null}
                        {isError ? <span style={{ color: 'red', fontWeight: '600' }}>{isError}</span> : null}
                        {!isError && showErrors && validationError ? <span style={{ color: 'red' }}>{validationError}</span> : null}
                        {isSuccess ? <span style={{ fontWeight: '600' }}>{isSuccess}</span> : null}
                        <FrappButton
                            className="submit"
                            type="submit"
                            id="new-btn"
                            disabled={isSaving || !!pendingAction}
                            handler={handleSave}
                        >
                            {isSaving ? 'Saving…' : 'Save'}
                        </FrappButton>
                    </div>
                </div>
            </div>
        </div>
    }

    const formRender = () => {
        const anyLanguageHasCallTransfer = getScriptsData.some(e => e.nodeData.data.callTransfer)
        return <div className="additional-wrapper2 call-transfer-form" style={{ padding: '2%' }}>
            <div className='tab-wrapper'>
                <div className='tab-container'>
                    {
                        getScriptsData.map((e, idx) => {
                            const isMissing = anyLanguageHasCallTransfer && !e.nodeData.data.callTransfer
                            return <div className='dc single-tab' key={idx}>
                                <div onClick={() => toggle(e)}>
                                    <a>
                                        <div className={selectedScript.name == e.name ? `each-tab active` : isMissing ? `each-tab error` : `each-tab`}>
                                            {e.name}
                                            {isMissing ? <img src={require('../../../assets/images/Interactive Script/redo_urgent.svg')} /> : null}
                                        </div>
                                    </a>
                                </div>
                            </div>
                        })
                    }
                </div>
            </div>
            {renderForm()}
        </div>
    }

    return formRender()
}

export default CallTransferForm
